import styled, { css } from 'styled-components';
import type { FieldValues, Path, UseFormRegister } from 'react-hook-form';
import type { ChangeEvent } from 'react';
import noop from 'lodash/noop';

type TCheckLabel = {
  disabled?: boolean;
};

type CheckMarkProps = {
  checked?: boolean;
};

type InputCheckProps<Fields extends FieldValues> = {
  name: Path<Fields>;
  id?: string;
  label?: string;
  value?: boolean;
  inputSize?: string;
  required?: boolean;
  disabled?: boolean;
  register?: UseFormRegister<Fields>;
  errorMessage?: string;
  defaultChecked?: boolean;
};

type DisplayCheckProps = {
  label?: string;
  value?: boolean;
  required?: boolean;
  disabled?: boolean;
  changeFunction?: (e: ChangeEvent<HTMLInputElement>) => void;
};

// Checkmark for input purposes (included in form)

const CheckMark = styled.span<CheckMarkProps>(
  ({ theme }) => css`
    position: absolute;
    top: 0;
    left: 0;
    height: 25px;
    width: 25px;
    border: ${theme.borders.form};

    &:after {
      content: '';
      position: absolute;
      display: none;
      left: 9px;
      top: 5px;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 3px 3px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
    }
  `
);

const CheckLabel = styled.label<TCheckLabel>(
  ({ theme, disabled }) => css`
    display: block;
    position: relative;
    padding-left: 40px;
    margin-bottom: 1.875rem;
    transition: 0.35s ease;

    ${!disabled &&
    css`
      &:hover {
        cursor: pointer;
      }
      @media (hover: hover) {
        &:hover {
          color: ${theme.colors.main.secondary};
          ${CheckMark} {
            background-color: ${theme.colors.lightWhiteSmoke};
          }
        }
      }
    `}
  `
);

const CheckInput = styled.input(
  ({ theme }) => css`
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;

    &:checked ~ ${CheckMark} {
      background-color: ${theme.colors.green};
      border: 0px;

      &:after {
        display: block;
      }
    }

    &:disabled ~ ${CheckMark} {
      border-color: ${theme.colors.darkGrey};
      background-color: ${theme.colors.lightWhiteSmoke};
    }

    &:disabled:checked ~ ${CheckMark} {
      background-color: ${theme.colors.darkGrey};
    }
  `
);

const ErrorWrapper = styled.span(
  ({ theme }) => css`
    display: block;
    color: ${theme.colors.main.error};
    font-size: ${theme.fonts.sizes.h6};
    margin-top: 5px;
  `
);

const InputCheck = <Fields extends FieldValues>({
  id,
  name,
  label,
  value,
  required,
  disabled,
  register,
  errorMessage,
  defaultChecked
}: InputCheckProps<Fields>) => {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const registerFunc = register ? register : () => {};
  return (
    <CheckLabel disabled={disabled}>
      <div>
        <span>
          <CheckInput
            id={id ? `${id}-input` : undefined}
            name={name}
            type="checkbox"
            checked={value}
            required={required}
            disabled={disabled}
            defaultChecked={defaultChecked}
            {...registerFunc(name)}
          />
          <CheckMark />
        </span>
        <span>{label}</span>
      </div>
      {errorMessage && <ErrorWrapper>{errorMessage}</ErrorWrapper>}
    </CheckLabel>
  );
};

// Checkmark for display purposes (not included in form)

const DisplayCheckMark = styled.span<CheckMarkProps>(
  ({ theme }) => css`
    position: absolute;
    top: 0;
    left: 0;
    height: 25px;
    width: 25px;
    border: ${theme.borders.form};

    &:after {
      content: '';
      position: absolute;
      display: none;
      left: 9px;
      top: 5px;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 3px 3px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
    }

    &.checked {
      background-color: ${theme.colors.green};
      border: 0px;

      &:after {
        display: block;
      }
    }
  `
);

const DisplayCheckLabel = styled.span`
  display: block;
  position: relative;
  padding-left: 35px;
  margin-bottom: 15px;
`;

const DisplayCheck = ({ value, changeFunction = noop }: DisplayCheckProps) => {
  return (
    // @TODO, displayCheckMark should be a select
    // @TODO, displayCheckLabel should be a <input type="checkbox" />
    <DisplayCheckLabel
      onClick={() =>
        changeFunction(!value as unknown as ChangeEvent<HTMLInputElement>)
      }
    >
      {value}
      <DisplayCheckMark className={value ? 'checked' : ''} />
    </DisplayCheckLabel>
  );
};

export { InputCheck, DisplayCheck };
